import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../redux/store";

/* ⭐ SAME SLICES AS FreshStoreHome / GroceryStoreHome — one food feed,
   one basket, one till. This counter scopes itself to Food › Fast Food. */
import {
  listenToPublishedFoods,
  filterLocalFoodsByCountry,
  setFilters,
  FoodItem,
} from "../../../redux/foodSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../redux/cartSlice";
import { type RateType, DEFAULT_RATE_TYPE, calculateLineTotal } from "../../../redux/foodServiceFeeSlice";
import { listenToBrandsByBusinessType } from "../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";

/* ⭐ BRAND + MARKETING ART */
import appLogo from "../../../assets/app/logo.png";
import { FoodMarketing, Delivery } from "../../../CATEGORIES/UICategories";

/* ⭐ CATEGORY SOURCE — FreshStore saves `categoryId` inside Food › Fast Food
   plus `attributes` keyed by attribute id. This page walks the tree back up. */
import {
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, ShoppingBag, X, ChevronRight, ChevronLeft, Plus, Minus,
  User, ShoppingCart, MapPin, Globe, AlertTriangle, Timer, Flame,
  Home, LayoutGrid, Check, ChevronDown, Clock, UtensilsCrossed, Leaf,
  WheatOff, Sparkles, Tag, Truck, ShieldCheck, Star, Soup,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE — the Fast Food counter of the Food department.
   department → category (cuisine) → subCategory → productType → product
═══════════════════════════════════════════════════════════ */
const FOOD_DEPT_ID = "08";
const FASTFOOD_BRANCH_ID = "0801";

/** Which commercePartners cook here. Flip if your seed data differs. */
const FOOD_BUSINESS_TYPE = 9;

/** Marketing art, straight from UICategories — no hardcoded paths. */
const HERO_IMAGE = (FoodMarketing?.[0] as any)?.image ?? "";
const DISH_IMAGE = (FoodMarketing?.[1] as any)?.image ?? HERO_IMAGE;
const COURIER_IMAGE = (Delivery?.[0] as any)?.image ?? "";

/* ═══════════════════════════════════════════════════════════
   APPETITE PALETTE
   Grocery is green and clean; this counter is the opposite —
   hot cream, ember, saffron, charred cocoa. Red belongs to the
   Heartlas mark and to price cuts, nothing else.
═══════════════════════════════════════════════════════════ */
const F = {
  bg: "#FFF6EA",
  panel: "#FFFFFF",
  crust: "#FFEFD9",

  ink: "#2A1608",
  inkSoft: "#7A5B41",
  inkFaint: "#B29477",

  ember: "#E2451F",
  emberDark: "#B22F10",
  emberSoft: "#FDE7DC",

  saffron: "#F5A524",
  saffronSoft: "#FDF0D6",

  basil: "#3F8A4C",
  basilSoft: "#E8F3E9",

  red: "#E01B24",
  redSoft: "#FDEAEB",

  border: "#F0DFC6",
  divider: "#F7EBD9",
};

const GRAD_EMBER = `linear-gradient(135deg, ${F.saffron} 0%, ${F.ember} 55%, ${F.emberDark} 100%)`;
const GRAD_HERO = `linear-gradient(100deg, rgba(42,22,8,.94) 0%, rgba(178,47,16,.82) 48%, rgba(226,69,31,.22) 100%)`;

/* accent comes from the kitchen's own brand, falling back to ember */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || F.ember;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry ALWAYS by parentId, never
   string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  const seen = new Set<string>();
  let cur = CAT_MAP.get(key);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).map((cid) => catName(cid)).join(" › ");

/** Is this listing plated in Food › Fast Food? */
const isFastFood = (id?: string | number | null) =>
  chainIdsOf(id).includes(FASTFOOD_BRANCH_ID);

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — resolved the way the seller screen resolves them,
   so the defs line up with the saved keys.
═══════════════════════════════════════════════════════════ */
const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const cacheKey = String(categoryId);
  const hit = attrCache.get(cacheKey);
  if (hit) return hit;

  const keys = [...chainIdsOf(categoryId)]
    .reverse()
    .flatMap((cid) => [`0${cid}`, cid]);

  let found: any[] = [];
  for (const key of keys) {
    try {
      const defs = attributesForCategory(key as any);
      if (defs && defs.length) { found = defs; break; }
    } catch {}
  }
  attrCache.set(cacheKey, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Only what the kitchen actually filled in — nothing invented. */
const savedAttrsOf = (item: any) => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .map((def: any) => ({ def, raw: saved[String(def.id)] }))
    .filter(({ raw }) => !isEmptyValue(raw));
};

/* ═══════════════════════════════════════════════════════════
   SIZES / EXTRAS — FreshStore writes `portions` as an array of
   {id,name,price,unit,stock}; older docs used a keyed map.
═══════════════════════════════════════════════════════════ */
export interface Portion { id: string; name: string; price: number; unit?: string; stock?: number }
export interface Extra { id: string; name: string; price: number }

const portionsOf = (item: any): Portion[] => {
  const raw = item?.portions;
  if (!raw) return [];
  const list: Portion[] = Array.isArray(raw)
    ? raw.map((p: any) => ({
        id: String(p?.id ?? p?.name ?? ""),
        name: p?.name ?? String(p?.id ?? ""),
        price: Number(p?.rate ?? p?.price) || 0,
        unit: p?.unit ?? "",
        stock: Number(p?.stock) || 0,
      }))
    : Object.entries(raw).map(([k, v]: [string, any]) => ({
        id: k,
        name: v?.name ?? k,
        price: Number(v?.rate ?? v?.price) || 0,
        unit: v?.unit ?? "",
        stock: Number(v?.stock) || 0,
      }));
  return list.filter((p) => p.id && p.name);
};

const extrasOf = (item: any): Extra[] =>
  Array.isArray(item?.extras)
    ? item.extras
        .map((e: any) => ({ id: String(e?.id ?? e?.name ?? ""), name: e?.name ?? "", price: Number(e?.rate ?? e?.price) || 0 }))
        .filter((e: Extra) => e.id && e.name)
    : [];

/* ── PORTIONS ──────────────────────────────────────────
   The listing's own price is just another portion. Put it in the
   list as "Regular" so choosing a size is a swap, never a surcharge. */
export const REGULAR_PORTION_ID = "__regular";

const regularPortionOf = (item: any): Portion => ({
  id: REGULAR_PORTION_ID,
  name: item?.portionLabel || "Regular",
  price: Number(item?.rate) || 0,
  unit: item?.unit ?? "",
  stock: Number(item?.stock) || 0,
});

const portionChoicesOf = (item: any): Portion[] => [regularPortionOf(item), ...portionsOf(item)];

/** What the cheapest and dearest way to order this dish costs. */
const priceRangeOf = (item: any) => {
  const prices = portionChoicesOf(item).map((p) => p.price);
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

/**
 * The FIRST portion picked prices the dish — a Large *is* the price, it isn't
 * base + surcharge. Anything picked after that is a second helping, so it
 * rides along as an extra beside the sauces.
 */
const resolveSelection = (item: any, sizeIds: string[], extraIds: string[]) => {
  const choices = portionChoicesOf(item);
  const picked = sizeIds
    .map((id) => choices.find((p) => p.id === id))
    .filter(Boolean) as Portion[];

  const basePortion = picked[0] ?? regularPortionOf(item);
  const extraPortions = picked.slice(1);
  const menuExtras = extrasOf(item).filter((e) => extraIds.includes(e.id));

  const extraLines: Extra[] = [
    ...extraPortions.map((p) => ({ id: `portion:${p.id}`, name: `Extra ${p.name}`, price: p.price })),
    ...menuExtras,
  ];

  return { basePortion, extraPortions, menuExtras, extraLines };
};

/** One serving, all in — the portion plus everything added to it. */
const computeItemPrice = (item: any, sizeIds: string[], extraIds: string[]) => {
  const { basePortion, extraLines } = resolveSelection(item, sizeIds, extraIds);
  return basePortion.price + extraLines.reduce((s, e) => s + e.price, 0);
};

const buildCartId = (item: any, sizeIds: string[], extraIds: string[]) => {
  const plain =
    (sizeIds.length === 0 || (sizeIds.length === 1 && sizeIds[0] === REGULAR_PORTION_ID)) &&
    extraIds.length === 0;
  if (plain) return item.listingId;
  const sizePart = sizeIds.length ? sizeIds.join("+") : "base"; // order matters — [0] prices it
  const extraPart = extraIds.length ? [...extraIds].sort().join("+") : "none";
  return `${item.listingId}__${sizePart}__${extraPart}`;
};

const buildCartLabel = (item: any, sizeIds: string[], extraIds: string[]) => {
  const { basePortion, extraPortions, menuExtras } = resolveSelection(item, sizeIds, extraIds);
  let label = item?.name || "";
  if (basePortion.id !== REGULAR_PORTION_ID)
    label += ` (${basePortion.name}${basePortion.unit ? ` · ${basePortion.unit}` : ""})`;
  const addOns = [...extraPortions.map((p) => `Extra ${p.name}`), ...menuExtras.map((e) => e.name)];
  if (addOns.length) label += ` + ${addOns.join(", ")}`;
  return label;
};

/**
 * Cart lines → the shape calculateFoodEarnings wants. Extras are stored on the
 * line PER SERVING (qty 1), so bumping quantity in the basket can't desync them;
 * they're multiplied back up to billing units here, at the till.
 */
export const buildFoodEarningsPayload = (cartItems: any[]) => {
  const items = cartItems.map((l: any) => ({
    itemId: l.baseListingId ?? l.listingId,
    name: l.name,
    rate: l.rate,
    rateType: (l.rateType as RateType) ?? DEFAULT_RATE_TYPE,
    qty: l.qty ?? 1,
    hostId: l.hostId,
    brandId: l.brandId,
  }));

  const extras = cartItems.flatMap((l: any) =>
    (l.extras ?? []).map((e: any) => ({
      itemId: l.baseListingId ?? l.listingId,
      name: e.name,
      rate: e.price,
      qty: (e.qty ?? 1) * (l.qty ?? 1),
      hostId: e.hostId ?? l.hostId,
      brandId: e.brandId ?? l.brandId,
    }))
  );

  return { items, extras };
};

/* ═══════════════════════════════════════════════════════════
   KITCHEN HELPERS
═══════════════════════════════════════════════════════════ */
const SPICE = ["None", "Mild", "Medium", "Hot"];

const isOutOfStock = (item: any) =>
  item?.available === false || (item?.stock ?? 0) <= 0;

const discountOf = (item: any): number | null => {
  const was = item?.compareAtPrice ?? 0;
  const now = item?.rate ?? 0;
  if (!(was > now) || was <= 0) return null;
  return Math.round(((was - now) / was) * 100);
};

const prepLabel = (item: any) =>
  item?.prepTimeMinutes ? `${item.prepTimeMinutes} min` : "";

const servingLabel = (item: any) => {
  const n = item?.netContent;
  const u = item?.unit;
  if (n && u) return `${n} ${u}`;
  if (u) return `per ${u}`;
  return "";
};

type SortKey = "default" | "fastest" | "price_asc" | "price_desc" | "name_asc";

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — prefixed fd- so this page can sit beside
   FreshStoreHome, GroceryStoreHome and StoreProductsHome.
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Archivo+Black&display=swap');
.fd-page *, .fd-page *::before, .fd-page *::after { box-sizing: border-box; }
.fd-page ::-webkit-scrollbar { display: none; }
.fd-page input, .fd-page button, .fd-page select { font-family: inherit; }

@keyframes fdPulse { 0%,100%{opacity:1} 50%{opacity:.35} }
@keyframes fdRise { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:none;} }
@keyframes fdSpinSlow { to { transform: rotate(360deg); } }
@keyframes fdSteam {
  0%   { opacity:0; transform: translateY(2px) scaleX(.9); }
  35%  { opacity:.75; }
  100% { opacity:0; transform: translateY(-11px) scaleX(1.15); }
}

.fd-fade { animation: fdRise .24s ease both; }

/* ── THE PLATE: a dish is a round thing, so the card is round ── */
.fd-plate { background:none; border:none; padding:0; cursor:pointer; display:flex; flex-direction:column; align-items:center; }
.fd-disc {
  position:relative; border-radius:50%; overflow:hidden;
  background:${F.crust};
  box-shadow: 0 10px 26px rgba(42,22,8,.14), inset 0 0 0 3px #fff;
  transition: transform .22s cubic-bezier(.34,1.56,.64,1), box-shadow .22s;
}
.fd-plate:hover .fd-disc { transform: translateY(-6px) rotate(-2deg); box-shadow: 0 18px 36px rgba(178,47,16,.26), inset 0 0 0 3px #fff; }
.fd-plate:active .fd-disc { transform: translateY(-2px) scale(.98); }
.fd-ring { position:absolute; inset:-4px; border-radius:50%; border:2px dashed ${F.border}; pointer-events:none; }
.fd-plate:hover .fd-ring { border-color:${F.saffron}; animation: fdSpinSlow 14s linear infinite; }

.fd-steam { position:absolute; top:-2px; left:50%; width:3px; height:12px; border-radius:3px; background:rgba(255,255,255,.85); animation: fdSteam 2.4s ease-in-out infinite; }

.fd-pop { border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; border-radius:50%; transition: transform .14s, box-shadow .18s; }
.fd-pop:hover { transform: scale(1.1) rotate(6deg); }
.fd-pop:active { transform: scale(.9); }

.fd-chip { display:inline-flex; align-items:center; gap:5px; font-size:10.5px; font-weight:800; padding:4px 9px; border-radius:999px; }

.fd-cuisine { background:none; border:none; cursor:pointer; flex-shrink:0; display:flex; flex-direction:column; align-items:center; gap:7px; width:76px; padding:2px; }
.fd-cuisineic { width:52px; height:52px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:21px; overflow:hidden; transition: transform .2s, box-shadow .2s; }
.fd-cuisine:hover .fd-cuisineic { transform: translateY(-4px) scale(1.06); box-shadow: 0 10px 20px rgba(178,47,16,.22); }
.fd-cuisinelabel { font-size:10.5px; font-weight:700; line-height:1.2; text-align:center; }

.fd-arrow { width:34px; height:34px; border-radius:50%; border:1px solid ${F.border}; background:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; box-shadow:0 4px 14px rgba(42,22,8,.12); }
.fd-arrow:hover { border-color:${F.ember}; }

.fd-kitchen { display:flex; flex-direction:column; gap:9px; background:#fff; border:1px solid ${F.border}; border-radius:20px; padding:14px; cursor:pointer; transition: border-color .18s, box-shadow .18s, transform .18s; }
.fd-kitchen:hover { border-color:${F.ember}; box-shadow:0 14px 30px rgba(42,22,8,.12); transform:translateY(-3px); }

.fd-treerow { display:flex; align-items:center; gap:10px; font-weight:600; font-size:13.5px; color:${F.ink}; cursor:pointer; padding:9px 10px; border-radius:12px; }
.fd-treerow:hover { background:${F.emberSoft}; }
.fd-treerow.on { background:${F.emberSoft}; color:${F.emberDark}; font-weight:800; }

.fd-tab { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:${F.inkFaint}; padding:4px 10px; }
.fd-tab.active { color:${F.ember}; }

@media (prefers-reduced-motion: reduce){
  .fd-fade, .fd-disc, .fd-ring, .fd-steam, .fd-pop, .fd-cuisineic { animation:none !important; transition:none !important; }
}
`;

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const FoodStoreHome: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const listings = useSelector((s: RootState) => (s as any).food.listings as FoodItem[]);
  const filteredListings = useSelector((s: RootState) => (s as any).food.filteredListings as FoodItem[]);
  const cartItems = useSelector((s: RootState) => s.cart.items);
  const ratingSummaries = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");

  /* cuisine selections inside Fast Food */
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [cravingsExpanded, setCravingsExpanded] = useState(true);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  /* detail sheet state */
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);

  const cuisineRef = useRef<HTMLDivElement | null>(null);
  const cravingRef = useRef<HTMLDivElement | null>(null);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth gate ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data listeners ── */
  useEffect(() => { dispatch(listenToPublishedFoods() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => {
    const unsub = dispatch(listenToBrandsByBusinessType(FOOD_BUSINESS_TYPE) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);
  useEffect(() => { dispatch(filterLocalFoodsByCountry(null) as any); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalFoodsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalFoodsByCountry(country) as any);
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false);
          dispatch(filterLocalFoodsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then((r) => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      }).catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalFoodsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveCat(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalFoodsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalFoodsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string | null) => {
    setActiveCat((p) => (id === null || p === id ? null : id));
    setActiveDeep(null);
  };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  /* One basket, many kitchens — a table rarely orders from one place. */
  const SINGLE_BRAND_CART_MODE = false;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  /**
   * One place builds the cart line, so the counter, the sheet and the
   * till never disagree about what's being ordered or what it costs.
   */
  const buildFoodCartLine = (item: any, sizeIds: string[], extraIds: string[], quantity: number) => {
    const rt: RateType = (item?.rateType as RateType) || DEFAULT_RATE_TYPE;
    const { basePortion, extraLines } = resolveSelection(item, sizeIds, extraIds);
    const listingId = buildCartId(item, sizeIds, extraIds);
    

    /* extras leave here as their own lines — the kitchen earns on them too */
    const extras = extraLines.map((e) => ({
  id: `${listingId}::${e.id}`,
  name: e.name,
  rate: e.price,
  qty: 1,                 // per serving; the till scales it
}));

    const extrasPerServing = +extras.reduce((s, e) => s + e.rate, 0).toFixed(2);

    return {
      listingId,
      baseListingId: item.listingId,
      name: buildCartLabel(item, sizeIds, extraIds),
      rate: basePortion.price,          // 🔥 the portion IS the rate — no stacking
      rateType: rt,
      qty: quantity,
      extras,                            // 🔥 now actually reaches foodServiceFeeSlice
      extrasPerServing,
      unitPrice: +(basePortion.price + extrasPerServing).toFixed(2),
      lineTotal: +(calculateLineTotal(basePortion.price, quantity, rt) + extrasPerServing * quantity).toFixed(2),
      photo: item.photos?.[0],
      hostId: item.hostId,
      brandId: item.brandId,
      unit: basePortion.unit || item.unit,
      netContent: item.netContent,
      portionId: basePortion.id !== REGULAR_PORTION_ID ? basePortion.id : undefined,
      portionIds: sizeIds.length ? sizeIds : undefined,
      extraIds: extraIds.length ? extraIds : undefined,
      categoryId: item.categoryId ?? "",
      attributes: item.attributes ?? {},
    };
  };

 const addLine = (item: any, sizeIds: string[], extraIds: string[], count = 1) => {
    if (isOutOfStock(item)) return;
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart(buildFoodCartLine(item, sizeIds, extraIds, Math.max(1, count)) as any));
  };

  const removeLine = (item: any, sizeIds: string[], extraIds: string[]) =>
    dispatch(decrementCartItem({ listingId: buildCartId(item, sizeIds, extraIds) }));
  const goToCheckout = (extra?: any) =>
    navigate(`/Freshpay/${userId}`, { state: { items: extra ? [...cartItems, extra] : cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/FreshStorebrand/${id}`); };

  const openDetail = (item: any) => {
    setDetailItem(item);
    setImgIndex(0);
    setQty(1);
    setSelectedExtras([]);
    setSelectedSizes([]); // nothing pre-selected — Regular is just one option among many
  };
  const closeDetail = () => setDetailItem(null);

  /* quick add from a plate — always adds at the default rate, no forced detour to the sheet */
  const quickAdd = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    addLine(item, [], [], 1);
  };

  const togglePortion = (id: string) =>
    setSelectedSizes((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );

  const scrollRail = (ref: React.RefObject<HTMLDivElement | null>, dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  /* ═══════════════════════════════════
     FILTERING — resolved through the ancestry chain of the
     item's saved categoryId, and always inside Fast Food.
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings ?? []).filter((l: any) => {
      if (!l.isPublished) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(FOOD_DEPT_ID)) return false;
      if (!chain.includes(FASTFOOD_BRANCH_ID)) return false;
      if (activeCat && !chain.includes(activeCat)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${(l.allergens ?? []).join(" ")} ${(l.tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "fastest")
      list = [...list].sort((a, b) => (a.prepTimeMinutes ?? 999) - (b.prepTimeMinutes ?? 999));

    return list;
  }, [filteredListings, activeCat, activeDeep, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || item.hostId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));

    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const cuisines = useMemo(() => childrenOf(FASTFOOD_BRANCH_ID), []);
  const activeFilterName = activeDeep ? catName(activeDeep) : activeCat ? catName(activeCat) : null;

  /* hot right now — best rated, then fastest out of the pass */
  const hotNow = useMemo(() => {
    return [...(listings ?? [])]
      .filter((l: any) => l.isPublished && !isOutOfStock(l) && isFastFood(l.categoryId))
      .sort((a: any, b: any) => {
        const ar = ratingSummaries[a.listingId]?.avgRating ?? 0;
        const br = ratingSummaries[b.listingId]?.avgRating ?? 0;
        if (ar !== br) return br - ar;
        return (a.prepTimeMinutes ?? 999) - (b.prepTimeMinutes ?? 999);
      })
      .slice(0, 14);
  }, [listings, ratingSummaries]);

  /* the kitchens actually cooking this counter near you */
  const kitchens = useMemo(() => {
    const cooking = new Set<string>();
    (listings ?? []).forEach((l: any) => {
      if (l.isPublished && isFastFood(l.categoryId)) cooking.add(l.brandId || l.hostId);
    });
    let list = (commercePartners || []).filter((p: any) => cooking.has(p.brandId));
    if (locationActive) {
      list = list.filter((p: any) => {
        const tier = brandTier.get(p.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...list]
      .sort((a: any, b: any) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3))
      .slice(0, 10);
  }, [commercePartners, listings, brandTier, locationActive, showGlobal]);

  const cravingTiles = useMemo(() => {
    const source = activeCat ? childrenOf(activeCat) : cuisines;
    return source.slice(0, 16);
  }, [activeCat, cuisines]);

  /* ═══════════════════════════════════
     HEADER  (plain function calls, not <Component />, so the
     search input keeps focus between keystrokes)
  ═══════════════════════════════════ */
  const brandMark = (size = 30) => (
    <button
      onClick={() => { resetAllFilters(); if (userId) navigate(`/FoodStoreHome/${userId}`); }}
      style={{ display: "flex", alignItems: "center", gap: 9, background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}
      aria-label="Heartlas Food home"
    >
      <img src={appLogo} alt="Heartlas" style={{ width: size, height: size, objectFit: "contain" }} />
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1 }}>
        <span style={{ fontFamily: "'Archivo Black','Montserrat',sans-serif", fontSize: size * 0.62, color: F.red, letterSpacing: "-0.02em" }}>
          heartlas
        </span>
        <span style={{ fontSize: size * 0.29, fontWeight: 800, letterSpacing: "0.46em", color: F.inkSoft, marginTop: 3 }}>
          FOOD
        </span>
      </span>
    </button>
  );

  const searchField = () => (
    <div style={{
      display: "flex", alignItems: "center", flex: 1, minWidth: 0, background: "#fff",
      border: `1.5px solid ${F.border}`, borderRadius: 999, padding: "5px 6px 5px 18px", gap: 8,
    }}>
      <input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Craving something? Search dishes or kitchens…"
        aria-label="Search dishes"
        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 13.5, color: F.ink, background: "transparent" }}
      />
      {searchQuery && (
        <button onClick={() => setSearchQuery("")} aria-label="Clear search"
          style={{ background: "none", border: "none", cursor: "pointer", color: F.inkFaint, display: "flex" }}>
          <X size={14} />
        </button>
      )}
      <button aria-label="Search"
        style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: GRAD_EMBER, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
        <Search size={16} color="#fff" />
      </button>
    </div>
  );

  const headerAction = (icon: React.ReactNode, label: string, action: () => void, badge?: number) => (
    <button onClick={action} aria-label={label}
      style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, color: F.ink, position: "relative", padding: "2px 4px" }}>
      {icon}
      <span style={{ fontSize: 10.5, fontWeight: 700 }}>{label}</span>
      {badge ? (
        <span style={{ position: "absolute", top: -5, right: -2, background: F.ember, color: "#fff", fontSize: 9, fontWeight: 900, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </button>
  );

  const deliverToButton = () => (
    <button
      onClick={locationDenied ? () => setLocationModalOpen(true) : handleToggleGlobal}
      style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", flexShrink: 0, textAlign: "left" }}
    >
      {locationLoading
        ? <span style={{ width: 8, height: 8, borderRadius: "50%", background: F.ember, animation: "fdPulse 1.2s infinite" }} />
        : locationDenied ? <AlertTriangle size={17} color={F.saffron} /> : showGlobal ? <Globe size={17} color={F.ember} /> : <MapPin size={17} color={F.ember} />}
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: F.inkFaint }}>Deliver to</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: F.ink }}>
          {locationLoading ? "Finding you…" : locationDenied ? "Set location" : showGlobal ? "Everywhere" : userCity && userCountry ? `${userCity}, ${userCountry}` : userCountry || "Nearby"}
        </span>
      </span>
    </button>
  );

  const headerBar = () => (
    <header style={{
      position: "sticky", top: 0, zIndex: 200, background: "#fff",
      borderBottom: `1px solid ${F.divider}`, boxShadow: "0 2px 14px rgba(42,22,8,.06)",
    }}>
      {isMobile ? (
        <div style={{ padding: "10px 14px 12px", display: "flex", flexDirection: "column", gap: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {brandMark(26)}
            <div style={{ flex: 1 }} />
            <button onClick={() => setCatOpen(true)} aria-label="Browse cuisines"
              style={{ background: "none", border: "none", cursor: "pointer", color: (activeCat || activeDeep) ? F.ember : F.ink, display: "flex", position: "relative" }}>
              <LayoutGrid size={20} />
              {(activeCat || activeDeep) && (
                <span style={{ position: "absolute", top: -1, right: -1, width: 7, height: 7, borderRadius: "50%", background: F.ember, border: "1.5px solid #fff" }} />
              )}
            </button>
            <button onClick={locationDenied ? () => setLocationModalOpen(true) : handleToggleGlobal} aria-label="Delivery location"
              style={{ background: "none", border: "none", cursor: "pointer", color: F.ink, display: "flex" }}>
              {showGlobal ? <Globe size={20} /> : <MapPin size={20} />}
            </button>
            <button onClick={() => goToCheckout()} aria-label="Basket"
              style={{ background: "none", border: "none", cursor: "pointer", color: F.ink, display: "flex", position: "relative" }}>
              <ShoppingCart size={20} />
              {cartTotal > 0 && (
                <span style={{ position: "absolute", top: -6, right: -7, background: F.ember, color: "#fff", fontSize: 9, fontWeight: 900, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {cartTotal > 99 ? "99+" : cartTotal}
                </span>
              )}
            </button>
          </div>
          {searchField()}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 22, padding: "14px 26px" }}>
          {brandMark(32)}
          {deliverToButton()}
          <div style={{ flex: 1, display: "flex", maxWidth: 620 }}>{searchField()}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            {headerAction(<Timer size={19} />, "Fastest", () => setActiveSort("fastest"))}
            {headerAction(<ShoppingBag size={19} />, "Orders", () => navigate(`/FreshOrders/${userId}`))}
            {headerAction(<User size={19} />, "Account", () => navigate(`/profile/${userId}`))}
            {headerAction(<ShoppingCart size={19} />, "Basket", () => goToCheckout(), cartTotal)}
          </div>
        </div>
      )}
    </header>
  );

  /* ═══════════════════════════════════
     CUISINE RAIL — every kitchen tradition under Fast Food
  ═══════════════════════════════════ */
  const cuisineRail = () => (
    <div style={{
      position: "relative", background: "#fff", border: `1px solid ${F.border}`,
      borderRadius: 22, padding: isMobile ? "12px" : "14px 18px", marginBottom: 18,
    }}>
      <div ref={cuisineRef} style={{ display: "flex", gap: 18, overflowX: "auto", scrollbarWidth: "none" as any }}>
        <button className="fd-cuisine" onClick={() => { handleCatSelect(null); closePanel(); }}>
          <span className="fd-cuisineic" style={{
            background: activeCat === null ? GRAD_EMBER : F.emberSoft,
            color: activeCat === null ? "#fff" : F.emberDark,
          }}>
            <UtensilsCrossed size={21} />
          </span>
          <span className="fd-cuisinelabel" style={{ color: activeCat === null ? F.emberDark : F.inkSoft, fontWeight: activeCat === null ? 800 : 700 }}>
            Everything
          </span>
        </button>

        {cuisines.map((node) => {
          const on = activeCat === node.id;
          return (
            <button key={node.id} className="fd-cuisine" onClick={() => handleCatSelect(node.id)}>
              <span className="fd-cuisineic" style={{
                background: on ? GRAD_EMBER : F.crust,
                color: on ? "#fff" : F.ember,
                boxShadow: on ? "0 8px 18px rgba(178,47,16,.3)" : "none",
              }}>
                {(node as any).image
                  ? <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : node.icon}
              </span>
              <span className="fd-cuisinelabel" style={{ color: on ? F.emberDark : F.inkSoft, fontWeight: on ? 800 : 700 }}>
                {node.name}
              </span>
            </button>
          );
        })}
      </div>

      {!isMobile && (
        <button className="fd-arrow" onClick={() => scrollRail(cuisineRef, 1)} aria-label="More cuisines"
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
          <ChevronRight size={17} color={F.ink} />
        </button>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     HERO — the craving, the promise, the rider
  ═══════════════════════════════════ */
  const heroBlock = () => (
    <div style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1.9fr 1fr",
      gap: isMobile ? 12 : 16,
      marginBottom: 26,
    }}>
      <div style={{
        position: "relative", borderRadius: 24, overflow: "hidden",
        minHeight: isMobile ? 196 : 220, background: F.ink, display: "flex", alignItems: "center",
      }}>
        {HERO_IMAGE && (
          <img src={HERO_IMAGE} alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: GRAD_HERO }} />

        <div style={{ position: "relative", padding: isMobile ? "20px 18px" : "30px 34px", maxWidth: 470 }}>
          <span className="fd-chip" style={{ background: "rgba(255,255,255,.16)", color: "#fff", marginBottom: 12 }}>
            <Flame size={11} /> Kitchens open now
          </span>
          <h1 style={{
            fontFamily: "'Archivo Black','Montserrat',sans-serif", color: "#fff", margin: "10px 0 0",
            fontSize: isMobile ? 26 : 38, lineHeight: 1.06, letterSpacing: "-0.025em",
          }}>
            Hot food,<br />at your door
          </h1>
          <p style={{ color: "rgba(255,255,255,.85)", fontSize: isMobile ? 11.5 : 13, fontWeight: 600, margin: "12px 0 0" }}>
            Cooked to order • Delivered in 15–30 min
          </p>
          <button
            onClick={() => { setActiveSort("fastest"); setActiveCat(null); setActiveDeep(null); }}
            style={{
              marginTop: 18, display: "inline-flex", alignItems: "center", gap: 8,
              background: "#fff", color: F.ink, border: "none", borderRadius: 999,
              padding: isMobile ? "10px 18px" : "12px 22px", fontSize: 12.5, fontWeight: 800, cursor: "pointer",
              boxShadow: "0 10px 24px rgba(0,0,0,.28)",
            }}>
            Order the fastest <ChevronRight size={15} />
          </button>
        </div>

        {!isMobile && DISH_IMAGE && (
          <div style={{
            position: "absolute", right: 26, top: "50%", transform: "translateY(-50%)",
            width: 168, height: 168, borderRadius: "50%", overflow: "hidden",
            border: "5px solid rgba(255,255,255,.9)", boxShadow: "0 20px 44px rgba(0,0,0,.35)",
          }}>
            <img src={DISH_IMAGE} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        )}
      </div>

      <div style={{ display: "grid", gap: isMobile ? 10 : 16, gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr" }}>
        <div style={{
          background: "#fff", border: `1px solid ${F.border}`, borderRadius: 20, padding: 16,
          display: "flex", alignItems: "center", gap: 12, overflow: "hidden",
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: F.ink }}>Rider on standby</p>
            <p style={{ margin: "6px 0 0", fontSize: 11.5, color: F.inkSoft, lineHeight: 1.5 }}>
              Straight from the pass in <strong style={{ color: F.ink }}>15–30 min</strong>
            </p>
          </div>
          {COURIER_IMAGE
            ? <img src={COURIER_IMAGE} alt="" style={{ width: 92, height: 62, objectFit: "contain", flexShrink: 0 }} />
            : <Truck size={34} color={F.ember} style={{ flexShrink: 0 }} />}
        </div>

        <div style={{
          background: GRAD_EMBER, borderRadius: 20, padding: 16, color: "#fff",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>Free delivery</p>
            <p style={{ margin: "6px 0 0", fontSize: 11.5, opacity: .9 }}>On orders over {price(25)}</p>
          </div>
          <span style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Soup size={22} />
          </span>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     ✨ THE PLATE — a dish is round, so its card is round
  ═══════════════════════════════════ */
  const dietDots = (item: any) => {
    const dots: React.ReactNode[] = [];
    const dot = (key: string, title: string, bg: string, color: string, node: React.ReactNode) => (
      <span key={key} title={title} style={{ width: 17, height: 17, borderRadius: "50%", background: bg, color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        {node}
      </span>
    );
    if (item.isVegan) dots.push(dot("vg", "Vegan", F.basilSoft, F.basil, <Leaf size={9} />));
    else if (item.isVegetarian) dots.push(dot("v", "Vegetarian", F.basilSoft, F.basil, <Sparkles size={9} />));
    if (item.isGlutenFree) dots.push(dot("gf", "Gluten-free", F.saffronSoft, F.saffron, <WheatOff size={9} />));
    if ((item.spiceLevel ?? 0) > 0) dots.push(dot("sp", SPICE[item.spiceLevel] || "Spicy", F.emberSoft, F.ember, <Flame size={9} />));
    return dots;
  };

  const plateCard = (item: any, opts?: { size?: number }) => {
    const disc = opts?.size ?? (isMobile ? 138 : 168);
    const soldOut = isOutOfStock(item);
    const { min: minPrice, max: maxPrice } = priceRangeOf(item);
    const q = soldOut ? 0 : getQty(item.listingId);
    const accent = brandAccentColor(brandMap.get(item.brandId));
    const summary = ratingSummaries[item.listingId];
    const off = discountOf(item);
    const prep = prepLabel(item);
    const partner = brandMap.get(item.brandId);

    return (
      <div key={item.listingId} className="fd-plate" onClick={() => openDetail(item)}
        style={{ width: disc + 22, flexShrink: 0, opacity: soldOut ? 0.55 : 1 }}>

        <div style={{ position: "relative", width: disc, height: disc, marginBottom: 12 }}>
          <div className="fd-disc" style={{ width: "100%", height: "100%" }}>
            {item.photos?.[0]
              ? <img src={item.photos[0]} alt={item.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", filter: soldOut ? "grayscale(1)" : "none" }}
                  onError={(e) => {
                    const el = e.currentTarget as HTMLImageElement;
                    el.src = appLogo; el.style.objectFit = "contain"; el.style.opacity = "0.35";
                  }} />
              : (
                <span style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <UtensilsCrossed size={disc * 0.26} color={F.ember} style={{ opacity: .3 }} />
                </span>
              )}
          </div>
          <span className="fd-ring" />

          {/* still steaming — only for what the kitchen turns out fast */}
          {!soldOut && item.prepTimeMinutes && item.prepTimeMinutes <= 15 && (
            <>
              <span className="fd-steam" style={{ marginLeft: -14, animationDelay: "0s" }} />
              <span className="fd-steam" style={{ animationDelay: ".5s" }} />
              <span className="fd-steam" style={{ marginLeft: 14, animationDelay: "1s" }} />
            </>
          )}

          {soldOut ? (
            <span className="fd-chip" style={{ position: "absolute", top: 4, left: -2, background: F.inkFaint, color: "#fff" }}>
              Sold out
            </span>
          ) : off ? (
            <span className="fd-chip" style={{ position: "absolute", top: 2, left: -4, background: F.red, color: "#fff", boxShadow: "0 6px 14px rgba(224,27,36,.35)" }}>
              -{off}%
            </span>
          ) : summary?.totalReviews >= 20 && summary.avgRating >= 4.6 ? (
            <span className="fd-chip" style={{ position: "absolute", top: 2, left: -4, background: F.saffron, color: F.ink, boxShadow: "0 6px 14px rgba(245,165,36,.4)" }}>
              <Star size={10} /> Top pick
            </span>
          ) : null}

          {/* the price sits on the rim like a tag on a plate */}
          <span style={{
            position: "absolute", bottom: 2, left: "50%", transform: "translateX(-50%)",
            background: "#fff", color: off ? F.red : F.ink, fontSize: 12.5, fontWeight: 900,
            padding: "5px 12px", borderRadius: 999, whiteSpace: "nowrap",
            boxShadow: "0 6px 16px rgba(42,22,8,.2)",
          }}>
            {maxPrice > minPrice ? "from " : ""}{price(minPrice)}
          </span>

          {/* add button hugs the rim */}
          {!soldOut && (
            q > 0 ? (
              <div onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute", right: -8, bottom: 16, display: "flex", alignItems: "center", gap: 5,
                  background: "#fff", borderRadius: 999, padding: "4px 6px", boxShadow: "0 6px 16px rgba(42,22,8,.22)",
                }}>
                <button className="fd-pop" aria-label="Remove one"
                  onClick={(e) => { e.stopPropagation(); removeLine(item, [], []); }}
                  style={{ width: 20, height: 20, background: F.crust, color: F.ember }}>
                  <Minus size={11} />
                </button>
                <span style={{ fontSize: 12, fontWeight: 900, color: F.ink, minWidth: 10, textAlign: "center" }}>{q}</span>
                <button className="fd-pop" aria-label="Add one" onClick={(e) => quickAdd(item, e)}
                  style={{ width: 20, height: 20, background: accent, color: "#fff" }}>
                  <Plus size={12} />
                </button>
              </div>
            ) : (
              <button className="fd-pop"
                aria-label={`Add ${item.name} to basket`}
                onClick={(e) => quickAdd(item, e)}
                style={{
                  position: "absolute", right: -2, bottom: 14, width: 34, height: 34,
                  background: accent, boxShadow: `0 8px 18px ${accent}66`, border: "2.5px solid #fff",
                }}>
                <Plus size={17} color="#fff" />
              </button>
            )
          )}
        </div>

        <div style={{ textAlign: "center", width: "100%", padding: "0 2px" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: F.ink, lineHeight: 1.28, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 34 }}>
            {item.name}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, marginTop: 5, fontSize: 10.5, color: F.inkFaint, fontWeight: 700, flexWrap: "wrap" }}>
            {prep && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Clock size={10} /> {prep}</span>}
            {summary?.totalReviews ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: F.saffron }}>
                <Star size={10} /> {summary.avgRating.toFixed(1)}
              </span>
            ) : null}
          </div>

          {partner?.name && (
            <div style={{ fontSize: 10, fontWeight: 700, color: F.inkFaint, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {partner.name}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 7, minHeight: 17 }}>
            {dietDots(item)}
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     HOT RIGHT NOW
  ═══════════════════════════════════ */
  const hotBlock = () => (
    <section style={{ marginBottom: 30 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ width: 34, height: 34, borderRadius: "50%", background: GRAD_EMBER, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Flame size={17} />
        </span>
        <h2 style={{ margin: 0, fontSize: isMobile ? 17 : 20, fontWeight: 900, color: F.ink, letterSpacing: "-0.025em" }}>
          Hot right now
        </h2>
        <div style={{ flex: 1 }} />
        {!isMobile && (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="fd-arrow" onClick={() => scrollRail(cravingRef, -1)} aria-label="Scroll left">
              <ChevronLeft size={16} color={F.ink} />
            </button>
            <button className="fd-arrow" onClick={() => scrollRail(cravingRef, 1)} aria-label="Scroll right">
              <ChevronRight size={16} color={F.ink} />
            </button>
          </div>
        )}
      </div>

      {hotNow.length === 0 ? (
        <div style={{ background: "#fff", border: `1px dashed ${F.border}`, borderRadius: 20, padding: 26, textAlign: "center", color: F.inkSoft, fontSize: 12.5 }}>
          The pass is quiet. Dishes land here the moment a kitchen puts them on the menu.
        </div>
      ) : (
        <div ref={cravingRef} style={{ display: "flex", gap: isMobile ? 14 : 20, overflowX: "auto", padding: "6px 2px 10px", scrollbarWidth: "none" as any }}>
          {hotNow.map((item: any) => plateCard(item, { size: isMobile ? 126 : 150 }))}
        </div>
      )}
    </section>
  );

  /* ═══════════════════════════════════
     KITCHENS NEAR YOU
  ═══════════════════════════════════ */
  const kitchensBlock = () => (
    <section style={{ marginBottom: 30 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 17 : 20, fontWeight: 900, color: F.ink, letterSpacing: "-0.025em" }}>
          Kitchens near you
        </h2>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate(`/FreshStores/${userId}`)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 800, color: F.ember }}>
          See all
        </button>
      </div>

      {kitchens.length === 0 ? (
        <div style={{ background: "#fff", border: `1px dashed ${F.border}`, borderRadius: 20, padding: 26, textAlign: "center", color: F.inkSoft, fontSize: 12.5 }}>
          {locationDenied
            ? "Turn on location and we'll show the kitchens that deliver to your door."
            : `No ${showGlobal ? "global" : "local"} kitchens cooking yet.`}
        </div>
      ) : (
        <div style={{
          display: isMobile ? "flex" : "grid",
          gridTemplateColumns: isMobile ? undefined : "repeat(auto-fill,minmax(214px,1fr))",
          gap: 12, overflowX: isMobile ? "auto" : undefined, paddingBottom: isMobile ? 4 : 0,
        }}>
          {kitchens.map((p: any) => {
            const rating = p.rating ?? p.avgRating;
            const reviews = p.totalReviews ?? p.reviewCount;
            const eta = p.deliveryTime || p.eta || "20–30 min";
            const accent = brandAccentColor(p);
            return (
              <div key={p.brandId} className="fd-kitchen" onClick={() => goToBrand(p.brandId)}
                style={{ width: isMobile ? 214 : undefined, flexShrink: isMobile ? 0 : undefined }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", border: `2px solid ${accent}33`, background: "#fff", flexShrink: 0 }}>
                    <img src={p.image || appLogo} alt={p.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: F.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name || "Kitchen"}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: F.inkSoft, fontWeight: 600, flexWrap: "wrap" }}>
                  {rating ? (
                    <span style={{ color: F.saffron, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <Star size={11} /> {Number(rating).toFixed(1)}
                      {reviews ? <span style={{ color: F.inkFaint, fontWeight: 600 }}>({reviews})</span> : null}
                    </span>
                  ) : null}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Clock size={11} /> {eta}</span>
                </div>

                <span className="fd-chip" style={{ background: F.emberSoft, color: F.emberDark, alignSelf: "flex-start" }}>
                  <Truck size={11} /> Free over {price(p.freeDeliveryOver ?? 25)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );

  /* ═══════════════════════════════════
     WHAT ARE YOU CRAVING — round cuisine tiles
  ═══════════════════════════════════ */
  const cravingsBlock = () => {
    if (!cravingTiles.length) return null;
    return (
      <section style={{ marginBottom: 30 }}>
        <div onClick={() => setCravingsExpanded((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 17 : 20, fontWeight: 900, color: F.ink, letterSpacing: "-0.025em" }}>
            {activeCat ? `Inside ${catName(activeCat)}` : "What are you craving?"}
          </h2>
          <motion.div animate={{ rotate: cravingsExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
            <ChevronDown size={19} color={F.inkSoft} />
          </motion.div>
        </div>

        <AnimatePresence initial={false}>
          {cravingsExpanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.24 }} style={{ overflow: "hidden" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill,minmax(${isMobile ? 92 : 118}px,1fr))`,
                gap: isMobile ? 12 : 18, padding: "4px 2px",
              }}>
                {cravingTiles.map((node) => {
                  const on = activeDeep === node.id || activeCat === node.id;
                  return (
                    <button key={node.id} className="fd-plate"
                      onClick={() => (activeCat ? handleDeepSelect(node.id) : handleCatSelect(node.id))}>
                      <span className="fd-disc" style={{
                        width: isMobile ? 76 : 92, height: isMobile ? 76 : 92,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: on ? GRAD_EMBER : F.crust, color: on ? "#fff" : F.ember,
                        fontSize: isMobile ? 24 : 30, marginBottom: 9,
                      }}>
                        {(node as any).image
                          ? <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : node.icon}
                        {on && (
                          <span style={{ position: "absolute", bottom: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 8px rgba(42,22,8,.25)" }}>
                            <Check size={12} color={F.ember} strokeWidth={3} />
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: on ? 900 : 700, color: on ? F.emberDark : F.inkSoft, textAlign: "center", lineHeight: 1.25 }}>
                        {node.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    );
  };

  /* ═══════════════════════════════════
     SIDEBAR TREE / MOBILE SHEET
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const picked = activeCat === node.id || activeDeep === node.id;

    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        <div className={`fd-treerow${picked ? " on" : ""}`}
          onClick={() => {
            if (depth === 0) handleCatSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}>
          <span style={{ display: "flex", color: picked ? F.ember : F.inkSoft }}>{node.icon}</span>
          <span style={{ flex: 1, minWidth: 0 }}>{node.name}</span>
          {kids.length > 0 ? (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }}>
              <ChevronRight size={14} color={F.inkFaint} />
            </motion.div>
          ) : picked ? <Check size={14} color={F.ember} /> : null}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const categoryTree = () => (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".14em", color: F.inkFaint }}>THE MENU</span>
        <div style={{ flex: 1 }} />
        {(activeCat || activeDeep) && (
          <button onClick={resetAllFilters}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, color: F.ember }}>
            Clear
          </button>
        )}
      </div>
      <div className={`fd-treerow${!activeCat && !activeDeep ? " on" : ""}`} onClick={() => { handleCatSelect(null); closePanel(); }}>
        <UtensilsCrossed size={15} />
        <span style={{ flex: 1 }}>Everything</span>
      </div>
      {cuisines.map((node) => <TreeNode key={node.id} node={node} depth={0} />)}
    </div>
  );

  const mobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(42,22,8,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "24px 24px 0 0", background: "#fff", zIndex: 401, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: F.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${F.divider}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 800, fontSize: 11, letterSpacing: ".15em", color: F.ember }}>CUISINES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close menu"
                style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={16} color={F.inkSoft} />
              </button>
            </div>
            <div style={{ padding: "14px 14px 32px" }}>{categoryTree()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     MENU BY KITCHEN
  ═══════════════════════════════════ */
  const brandRowHeader = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const shopName = partner?.name || "Kitchen";
    const shopLogo = partner?.image || appLogo;
    const city = partner?.address?.city;
    const accent = brandAccentColor(partner);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 14px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${shopName}`}
          style={{ width: 36, height: 36, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `2px solid ${accent}33`, background: "#fff", padding: 0, cursor: "pointer" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)}
          style={{ fontWeight: 900, fontSize: 14.5, color: F.ink, cursor: "pointer", textDecoration: "underline", textDecorationColor: accent, textDecorationThickness: 2, textUnderlineOffset: 5 }}>
          {shopName}
        </span>
        {city && (
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: F.inkFaint }}>
            <MapPin size={10} /> {city}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => goToBrand(brandId)}
          aria-label={`See ${shopName}'s full menu`}
          style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${F.border}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={accent} />
        </motion.button>
      </div>
    );
  };

  const emptyState = (title: string, body: string, cta: string, action: () => void) => (
    <div style={{ textAlign: "center", padding: 54, background: "#fff", border: `1px solid ${F.border}`, borderRadius: 22 }}>
      <div style={{ width: 56, height: 56, borderRadius: "50%", background: F.emberSoft, color: F.ember, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
        <UtensilsCrossed size={24} />
      </div>
      <h3 style={{ fontWeight: 900, fontSize: 19, color: F.ink, margin: "0 0 8px" }}>{title}</h3>
      <p style={{ color: F.inkSoft, margin: "0 0 20px", fontSize: 13 }}>{body}</p>
      <button onClick={action}
        style={{ padding: "12px 26px", borderRadius: 999, border: "none", background: GRAD_EMBER, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
        {cta}
      </button>
    </div>
  );

  const menuBlock = () => (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 4, height: 20, borderRadius: 2, background: GRAD_EMBER }} />
          <h2 style={{ margin: 0, fontSize: isMobile ? 17 : 20, fontWeight: 900, color: F.ink, letterSpacing: "-0.025em" }}>
            {activeFilterName ?? (locationDenied ? "All dishes" : showGlobal ? "Global kitchens" : userCity ? `Cooking near ${userCity}` : userCountry ?? "All dishes")}
          </h2>
          <span className="fd-chip" style={{ background: F.saffronSoft, color: F.inkSoft }}>
            {visibleCount} dishes · {productsByBrand.length} kitchens
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort dishes"
            style={{ border: `1px solid ${F.border}`, borderRadius: 999, padding: "6px 12px", fontSize: 11.5, fontWeight: 700, color: F.inkSoft, background: "#fff", cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="fastest">Out of the kitchen fastest</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal}
              style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#fff" : F.emberSoft, borderRadius: 999, padding: "6px 13px", border: `1px solid ${showGlobal ? F.border : "transparent"}`, fontSize: 11.5, fontWeight: 800, color: showGlobal ? F.inkSoft : F.emberDark, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show local" : "Show global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0 ? (
          activeFilterName
            ? emptyState(`Nobody's cooking ${activeFilterName}`, "Try another cuisine, or clear the filters to see the whole menu.", "Clear filters", resetAllFilters)
            : emptyState("No dishes on the menu yet", "Kitchens publish here as they open — check back shortly.", "Clear filters", resetAllFilters)
        ) : locationDenied ? (
          emptyState("Turn on location", "We use it to show kitchens that can actually deliver to you.", "Enable location", () => setLocationModalOpen(true))
        ) : (
          emptyState(
            showGlobal ? "No global kitchens right now" : "No local kitchens right now",
            `Switch to ${showGlobal ? "local" : "global"} above, or clear your filters.`,
            "Clear filters",
            resetAllFilters
          )
        )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {brandRowHeader(group.brandId)}
            <div style={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fill,minmax(${isMobile ? 150 : 186}px,1fr))`,
              gap: isMobile ? 14 : 22,
              justifyItems: "center",
            }}>
              {group.items.map((item: any) => plateCard(item))}
            </div>
          </div>
        ))
      )}
    </section>
  );

  /* ═══════════════════════════════════
     DISH DETAIL — sheet on mobile, modal on desktop
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;

    const item = detailItem;
    const photos: string[] = item.photos?.length ? item.photos : [];
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const sizes = portionChoicesOf(item); // Regular sits in the list like any other size
    const extras = extrasOf(item);

    const detailRows = savedAttrsOf(item)
      .filter(({ def }) => def.inputType !== "color")
      .map(({ def, raw }) => ({ name: def.name, value: resolveAttrValue(def, raw) }))
      .filter((r) => r.value);

    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const cartId = buildCartId(item, selectedSizes, selectedExtras);
    const inCart = cartItems.find((i: any) => i.listingId === cartId)?.qty ?? 0;
    const rt: RateType = (item?.rateType as RateType) || DEFAULT_RATE_TYPE;
    const rawUnit = computeItemPrice(item, selectedSizes, selectedExtras);
    const lineTotal = `${cur}${fx(calculateLineTotal(rawUnit, qty, rt)).toFixed(2)}`;

    const off = discountOf(item);
    const prep = prepLabel(item);
    const serving = servingLabel(item);
    const allergens: string[] = Array.isArray(item.allergens) ? item.allergens : [];
    const partner = brandMap.get(item.brandId);

    const canAdd = !soldOut; // picking a portion is optional

    const closeBtn = (
      <button onClick={closeDetail} aria-label="Close"
        style={{ background: "rgba(255,255,255,.94)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(42,22,8,.22)" }}>
        <X size={15} color={F.ink} />
      </button>
    );

    const chip = (key: string, bg: string, color: string, icon: React.ReactNode, text: string) => (
      <span key={key} className="fd-chip" style={{ background: bg, color, fontSize: 11, padding: "5px 10px" }}>
        {icon} {text}
      </span>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "26px 26px 0 0" : 22, overflow: "hidden", maxWidth: isMobile ? "100%" : 470, width: "100%", maxHeight: isMobile ? "88vh" : "84vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_EMBER, flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: F.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "18px 20px 0" }}>
          {photos.length > 0 ? (
            <div style={{ position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: 18, overflow: "hidden", marginBottom: 16, background: F.crust }}>
              <img src={photos[imgIndex]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {off && (
                <div className="fd-chip" style={{ position: "absolute", top: 10, left: 10, background: F.red, color: "#fff" }}>-{off}%</div>
              )}
              <div style={{ position: "absolute", top: 10, right: 10 }}>{closeBtn}</div>
              {photos.length > 1 && (
                <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
                  {photos.map((_, i) => (
                    <button key={i} aria-label={`Photo ${i + 1}`} onClick={() => setImgIndex(i)}
                      style={{ width: i === imgIndex ? 16 : 6, height: 6, borderRadius: 3, border: "none", padding: 0, cursor: "pointer", background: i === imgIndex ? F.ember : "rgba(255,255,255,.8)" }} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>{closeBtn}</div>
          )}

          {partner?.name && (
            <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: brandAccentColor(partner), margin: "0 0 5px", cursor: "pointer" }}
              onClick={() => { closeDetail(); goToBrand(item.brandId); }}>
              {partner.name}
            </p>
          )}

          {item.categoryId && (
            <p style={{ fontSize: 10.5, fontWeight: 700, color: F.inkFaint, margin: "0 0 6px", display: "flex", alignItems: "center", gap: 5 }}>
              <Tag size={10} /> {catPathLabel(item.categoryId)}
            </p>
          )}

          <div style={{ fontSize: 17, fontWeight: 800, color: F.ink, marginBottom: 8 }}>{item.name}</div>

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 21, fontWeight: 900, color: off ? F.red : F.ink }}>{price(rawUnit)}</span>
            {off && (
              <span style={{ fontSize: 13, color: F.inkFaint, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>
            )}
            {serving && <span style={{ fontSize: 12, fontWeight: 800, color: F.inkSoft }}>{serving}</span>}
            {hasRating && (
              <span style={{ fontSize: 12, fontWeight: 700, color: F.inkSoft }}>
                {summary.avgRating.toFixed(1)}★ ({summary.totalReviews.toLocaleString()})
              </span>
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {prep && chip("pt", F.crust, F.inkSoft, <Timer size={11} />, `${prep} to cook`)}
            {item.isVegan && chip("vg", F.basilSoft, F.basil, <Leaf size={11} />, "Vegan")}
            {!item.isVegan && item.isVegetarian && chip("v", F.basilSoft, F.basil, <Sparkles size={11} />, "Vegetarian")}
            {item.isGlutenFree && chip("gf", F.saffronSoft, F.saffron, <WheatOff size={11} />, "Gluten-free")}
            {(item.spiceLevel ?? 0) > 0 && chip("sp", F.emberSoft, F.ember, <Flame size={11} />, SPICE[item.spiceLevel] || "Spicy")}
            {item.calories ? chip("kc", F.crust, F.inkSoft, <Soup size={11} />, `${item.calories} kcal`) : null}
          </div>

          {/* ── SIZES — one choice ── */}
          {/* ── PORTIONS — first pick prices the dish, the rest are helpings ── */}
          {sizes.length > 1 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".18em", color: F.inkFaint, textTransform: "uppercase" }}>Portion</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: F.inkFaint }}>
                  Optional · pick a second for another helping
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sizes.map((s) => {
                  const idx = selectedSizes.indexOf(s.id);
                  const on = idx !== -1;
                  const isPrimary = idx === 0;
                  return (
                    <button key={s.id} onClick={() => togglePortion(s.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 14, border: `1.5px solid ${on ? F.ember : F.border}`, background: on ? F.emberSoft : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span style={{ width: 18, height: 18, borderRadius: 6, border: `2px solid ${on ? F.ember : F.inkFaint}`, background: on ? F.ember : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <Check size={12} color="#fff" />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: F.ink, textAlign: "left" }}>
                          {s.name}{s.unit ? ` · ${s.unit}` : ""}
                        </span>
                        {isPrimary && (
                          <span className="fd-chip" style={{ background: "#fff", color: F.emberDark, fontSize: 9.5, padding: "3px 7px" }}>
                            Sets the price
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? F.emberDark : F.inkSoft, flexShrink: 0 }}>
                        {on && !isPrimary ? `+${price(s.price)}` : price(s.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── EXTRAS — many ── */}
          {extras.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".18em", color: F.inkFaint, marginBottom: 10, textTransform: "uppercase" }}>Add extras</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {extras.map((ex) => {
                  const on = selectedExtras.includes(ex.id);
                  return (
                    <button key={ex.id}
                      onClick={() => setSelectedExtras((s) => (on ? s.filter((id) => id !== ex.id) : [...s, ex.id]))}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 14, border: `1.5px solid ${on ? F.ember : F.border}`, background: on ? F.emberSoft : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: 6, border: `2px solid ${on ? F.ember : F.inkFaint}`, background: on ? F.ember : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <Check size={12} color="#fff" />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: F.ink }}>{ex.name}</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? F.emberDark : F.inkSoft }}>+{price(ex.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: F.inkSoft, lineHeight: 1.65, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {/* ── what the kitchen filled in ── */}
          {(() => {
            const rows: [string, string][] = detailRows.map((d) => [d.name, d.value]);
            if (item.calories) rows.push(["Calories", `${item.calories} kcal`]);
            if (item.prepTimeMinutes) rows.push(["Prep time", `${item.prepTimeMinutes} min`]);
            if (allergens.length) rows.push(["Allergens", allergens.join(", ")]);
            if (!rows.length) return null;
            return (
              <div style={{ border: `1px solid ${F.border}`, borderRadius: 16, overflow: "hidden", marginBottom: 16 }}>
                {rows.map(([k, v], i) => (
                  <div key={`${k}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 13px", background: i % 2 ? "#fff" : F.bg, fontSize: 12 }}>
                    <span style={{ color: F.inkFaint, fontWeight: 700 }}>{k}</span>
                    <span style={{ color: F.ink, fontWeight: 700, textAlign: "right" }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {soldOut ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: F.inkFaint, marginBottom: 12 }}>Off the menu for now</p>
          ) : stock <= 5 ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: F.ember, marginBottom: 12 }}>Only {stock} portions left today</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: F.inkFaint }}>QTY</span>
              <div style={{ display: "flex", border: `1.5px solid ${F.border}`, borderRadius: 999, overflow: "hidden", background: F.bg }}>
                <button aria-label="Decrease quantity" onClick={() => setQty((v) => Math.max(1, v - 1))}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", color: F.ember, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={14} />
                </button>
                <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 900, color: F.ink }}>{qty}</div>
                <button aria-label="Increase quantity" onClick={() => setQty((v) => Math.min(stock || 99, v + 1))} disabled={qty >= (stock || 99)}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: qty >= (stock || 99) ? "not-allowed" : "pointer", color: F.ember, opacity: qty >= (stock || 99) ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={14} />
                </button>
              </div>
              {inCart > 0 && (
                <button onClick={() => removeLine(item, selectedSizes, selectedExtras)}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, color: F.ember }}>
                  {inCart} in basket — remove one
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${F.divider}` }}>
          <button disabled={!canAdd}
            onClick={() => { addLine(item, selectedSizes, selectedExtras, qty); closeDetail(); }}

            style={{ flex: 1, padding: "13px 0", borderRadius: 999, border: `1.5px solid ${F.ink}`, background: "transparent", color: F.ink, fontWeight: 700, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed", opacity: canAdd ? 1 : 0.45 }}>
           {soldOut ? "Sold out" : "Add to basket"}
          </button>
          <button disabled={!canAdd}
            onClick={() => {
              const line = buildFoodCartLine(item, selectedSizes, selectedExtras, qty);
              addLine(item, selectedSizes, selectedExtras, qty);
              closeDetail();
              goToCheckout(line);
            }}
            style={{ flex: 1.25, padding: "13px 0", borderRadius: 999, border: "none", background: canAdd ? GRAD_EMBER : "#C9C9C9", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed" }}>
            Checkout · {lineTotal}
          </button>
        </div>
      </div>
    );

    return createPortal(
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(42,22,8,.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }}
                style={{ width: 470, maxWidth: "100%", borderRadius: 22, overflow: "hidden", boxShadow: "0 32px 80px rgba(42,22,8,.4)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  };

  /* ═══════════════════════════════════
     LOCATION MODAL · TRUST STRIP · TABS · BASKET BAR
  ═══════════════════════════════════ */
  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(42,22,8,.8)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={(e) => e.stopPropagation()}
            style={{ width: 320, borderRadius: 22, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(42,22,8,.5)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD_EMBER, marginBottom: 20 }} />
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: F.emberSoft, color: F.ember, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <MapPin size={24} />
            </div>
            <h3 style={{ fontWeight: 900, margin: "0 0 8px", color: F.ink }}>Location is off</h3>
            <p style={{ color: F.inkSoft, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>
              Allow location in your browser settings to see the kitchens that deliver to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)}
              style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD_EMBER, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 12.5 }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const trustStrip = () => (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10,
      background: "#fff", border: `1px solid ${F.border}`, borderRadius: 18,
      padding: "12px 10px", marginBottom: 22,
    }}>
      {[
        { icon: <Timer size={15} />, title: "Cooked to order", body: "15–30 min" },
        { icon: <Truck size={15} />, title: "Free delivery", body: `Over ${price(25)}` },
        { icon: <ShieldCheck size={15} />, title: "Secure payment", body: "100% safe" },
      ].map((t) => (
        <div key={t.title} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ color: F.ember, display: "flex", flexShrink: 0 }}>{t.icon}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 10.5, fontWeight: 800, color: F.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</span>
            <span style={{ display: "block", fontSize: 9.5, color: F.inkFaint, fontWeight: 600 }}>{t.body}</span>
          </span>
        </div>
      ))}
    </div>
  );

  

  const mobileBasketBar = () => (isMobile && cartTotal > 0 && !detailItem ? (
    <button onClick={() => goToCheckout()} className="fd-fade"
      style={{
        position: "fixed", left: 14, right: 14, bottom: 76, zIndex: 120,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        padding: "13px 18px", borderRadius: 999, border: "none", cursor: "pointer",
        background: GRAD_EMBER, color: "#fff", boxShadow: "0 14px 30px rgba(178,47,16,.42)",
      }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800 }}>
        <ShoppingCart size={16} /> {cartTotal} {cartTotal === 1 ? "item" : "items"}
      </span>
      <span style={{ fontSize: 13, fontWeight: 900 }}>Checkout</span>
    </button>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  const mainColumn = () => (
    <>
      {cuisineRail()}
      {heroBlock()}
      {isMobile && trustStrip()}
      {hotBlock()}
      {kitchensBlock()}
      {cravingsBlock()}
      {menuBlock()}
    </>
  );

  return (
    <div className="fd-page" style={{
      minHeight: "100vh", background: F.bg, paddingBottom: isMobile ? 150 : 40,
      fontFamily: "'Montserrat',sans-serif", color: F.ink,
    }}>
      <style>{GLOBAL_CSS}</style>

      {headerBar()}
      {mobileCategorySheet()}

      {!isMobile ? (
        <div style={{
          display: "grid", gap: 22, padding: "22px 26px 0",
          gridTemplateColumns: desktopCatOpen ? "268px 1fr" : "1fr",
          maxWidth: 1440, margin: "0 auto",
        }}>
          {desktopCatOpen && (
            <aside style={{
              background: "#fff", border: `1px solid ${F.border}`, borderRadius: 22, padding: 16,
              position: "sticky", top: 96, maxHeight: "calc(100vh - 120px)", overflowY: "auto",
            }}>
              {categoryTree()}
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <button onClick={() => setDesktopCatOpen((v) => !v)}
                style={{ display: "flex", alignItems: "center", gap: 7, background: "#fff", border: `1px solid ${F.border}`, borderRadius: 999, padding: "8px 14px", fontSize: 12, fontWeight: 800, color: F.ink, cursor: "pointer" }}>
                <LayoutGrid size={14} color={F.ember} /> {desktopCatOpen ? "Hide the menu" : "Browse the menu"}
              </button>
              {activeFilterName && (
                <span className="fd-chip" style={{ background: F.emberSoft, color: F.emberDark, fontSize: 11.5, padding: "6px 12px" }}>
                  {activeFilterName}
                  <button onClick={resetAllFilters} aria-label="Clear filter"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", display: "flex", padding: 0, marginLeft: 2 }}>
                    <X size={11} />
                  </button>
                </span>
              )}
              <div style={{ flex: 1 }} />
            </div>
            {mainColumn()}
          </main>
        </div>
      ) : (
        <div style={{ padding: "14px 14px 0" }}>{mainColumn()}</div>
      )}

      {detailModal()}
      {locationModal()}
      {mobileBasketBar()}
    
    </div>
  );
};

export default FoodStoreHome;
